/**
 * @file index.tsx
 *
 * @fileoverview Renders the job detail form (for adding or editing a job).
 */
import React, { useContext } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import EntityForm from '~/jobiqo/components/Form/EntityForm';
import { useJobFormExtraInitialValues } from '~/jobiqo/components/Slots/JobFormExtraInitialValuesSlot';
import { CenterBox } from '~/vcl/atoms/06-layout/01-alignment/02-center-box';
import {
  getIsJobInPendingState,
  getIsJobWorkflowPublished,
  getIsJobWorkflowPaused,
} from '~/jobiqo/components/Job/Detail/utils';
import { JobFormButtons } from '~/jobiqo/components/Job/Form/components/Buttons';
import { useJobDurationInfo } from '~/jobiqo/components/Job/Form/hooks/useJobDurationInfo';
import useJobFormFieldGroups from '~/jobiqo/components/Job/Form/hooks/useJobFormFieldGroups';
import useJobPageMeta from '~/jobiqo/components/Job/Form/hooks/useJobPageMeta';
import {
  getJobFormApplicationOverrides,
  getJobFormApplicationValidationSchema,
} from '~/jobiqo/components/Job/Form/utils/applications';
import { getAutoSuggestionJobFormOverrides } from '~/jobiqo/components/Job/Form/utils/autoSuggestions';
import { getJobFormJobPerLinkOverrides } from '~/jobiqo/components/Job/Form/utils/jobPerLink';
import {
  getJobSchedulingValidationSchema,
  getSheduledJobFormProps,
} from '~/jobiqo/components/Job/Form/utils/scheduledJob';
import { assignFieldsToHide } from '~/jobiqo/components/Job/utils/edit';
import { Product, WorkflowState } from '~/jobiqo/types/types';
import { getCommonTranslations } from '~/jobiqo/utils/intl';
import { Main } from '~/vcl/atoms/06-layout/09-main';
import { getAIJobDescription } from './utils/aiJobDescription';
import {
  getJobFormQuickApplyValidationSchema,
  getJobQuickApplyOverrides,
} from './utils/quickApply';
import { UserContext, UserContextInterface } from '~/jobiqo/context/user';
import { getAgencyOrganizationFields } from '~/jobiqo/components/Job/Form/utils/agencyOrganizationFields';
import { JobPreview } from '~/jobiqo/components/Job/Wizard/components/JobPreview';
import styles from './jobForm.module.scss';

export interface JobFormProps {
  /**
   * The job type to show.
   */
  jobType: string;
  /**
   * The entity in the case of an edit form.
   */
  entity?: any;
  /**
   * Option to show buttons or not.
   *
   * @default "true"
   */
  showButtons?: boolean;
  /**
   * Contents to go inside.
   */
  children?: any;
  /**
   * Save form label.
   */
  saveLabel?: React.ReactNode;
  /**
   * If the job is being submitted.
   *
   * @default "false"
   */
  submittingJobMutation?: boolean;
  /**
   * The job's workflow state.
   */
  workflowState?: WorkflowState;
  /**
   * The flag indicating if we are republishing a job.
   */
  republishing?: boolean;
  /**
   * Callback for when submitting the form.
   */
  onSubmit?: (data: {} | React.FormEvent) => void;
  /**
   * Callback for when cancelling the form submission.
   */
  onCancel?: () => void;
  /**
   * Callback for when removing / deleting an entity.
   */
  onRemove?: () => void;
  /**
   * The bundle information if the job is being posted with bundle.
   */
  bundle?: Product;
  /**
   * The list of errors for the form.
   */
  fieldErrorList?: any[];
}

export const JobForm = ({
  jobType,
  onSubmit,
  onCancel,
  entity,
  showButtons = true,
  saveLabel,
  submittingJobMutation = false,
  workflowState,
  republishing,
  bundle,
  fieldErrorList,
  ...props
}: JobFormProps) => {
  const intl = useIntl();
  const { user }: UserContextInterface = useContext(UserContext);
  const jobDurationInfo = useJobDurationInfo(jobType);
  const fieldGroups = useJobFormFieldGroups(jobType);
  const { isEditPage } = useJobPageMeta();
  const isJobPublished = getIsJobWorkflowPublished(workflowState);
  const isJobPaused = getIsJobWorkflowPaused(workflowState);
  const isJobInPendingState = getIsJobInPendingState(workflowState);
  const commonTranslations = getCommonTranslations();
  const {
    values: extraInitialValues,
    loading: loadingExtraInitialValues,
  } = useJobFormExtraInitialValues(user, entity);

  const scheduledJobFormProps = getSheduledJobFormProps({
    isEditPage,
    isJobPublished,
    isJobPaused,
    republishing,
    entity,
    maximumJobDuration: jobDurationInfo?.maximumJobDuration ?? 0,
    extendedRuntime: bundle?.extendedDuration,
  });
  const overrides = {
    ...getJobFormApplicationOverrides(),
    ...getJobFormJobPerLinkOverrides(jobType, intl),
    ...getAutoSuggestionJobFormOverrides(),
    // If we are on the job edit page, do not allow
    // recruiter to change the fields set to not show on edit.
    ...assignFieldsToHide(
      isEditPage,
      isJobPublished || isJobInPendingState,
      jobType,
    ),
    ...getAIJobDescription(user),
    ...getJobQuickApplyOverrides(),
    ...getAgencyOrganizationFields(user, entity),
  };
  const extraValidationSchema = {
    ...getJobFormApplicationValidationSchema(
      intl,
      jobDurationInfo?.maximumJobDuration ?? 0,
    ),
    ...getJobSchedulingValidationSchema(
      intl,
      jobDurationInfo?.maximumJobDuration ?? 0,
      bundle?.extendedDuration ?? undefined,
      entity,
      isEditPage && isJobPublished && !republishing,
    ),
    ...getJobFormQuickApplyValidationSchema(commonTranslations),
  };

  if (loadingExtraInitialValues) {
    return (
      <Main>
        <CenterBox>
          <FormattedMessage
            id="form_loading"
            defaultMessage="Loading form..."
          />
        </CenterBox>
      </Main>
    );
  }

  return (
    <Main>
      <div className="job-form">
        <EntityForm
          showLabel={false}
          name="node"
          bundle={jobType}
          {...(entity ? { entity } : null)}
          fieldGroups={fieldGroups}
          {...scheduledJobFormProps}
          overrides={overrides}
          extraValidationSchema={extraValidationSchema}
          extraInitialValues={extraInitialValues}
          fieldErrorList={fieldErrorList}
          onCancel={onCancel}
          onSubmit={onSubmit}
          renderButtons={(props) => (
            <JobFormButtons
              {...props}
              showButtons={showButtons}
              submittingJobMutation={submittingJobMutation}
              saveLabel={saveLabel}
            />
          )}
          {...props}
        >
          <div className={styles.previewContainer}>
            <JobPreview jobType={jobType} isEdit={isEditPage} />
          </div>
        </EntityForm>
      </div>
    </Main>
  );
};
